"use client";

import { useState, useEffect } from "react";
import { Plus, X, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface AppTile {
  id: string;
  name: string;
  url: string;
  icon: string;
}

const STORAGE_KEY = "doliui_app_tiles";

const EMOJI_PICKER = [
  "🏠", "📧", "📊", "🔧", "🦊", "☁️", "🐳", "📋",
  "🔗", "💼", "🖥️", "📁", "🛡️", "⚙️", "🌐", "📡",
];

const SUGGESTIONS = [
  { name: "Homarr", icon: "🏠" },
  { name: "GitLab", icon: "🦊" },
  { name: "Grafana", icon: "📊" },
  { name: "Nextcloud", icon: "☁️" },
  { name: "Portainer", icon: "🐳" },
  { name: "Mail", icon: "📧" },
  { name: "Dolibarr", icon: "📋" },
];

function AddAppModal({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (tile: Omit<AppTile, "id">) => void;
}) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [icon, setIcon] = useState("🔗");

  const handleSubmit = () => {
    if (!name.trim() || !url.trim()) return;
    const finalUrl = url.startsWith("http") ? url : `https://${url}`;
    onAdd({ name: name.trim(), url: finalUrl, icon });
    setName("");
    setUrl("");
    setIcon("🔗");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajouter une application</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Nom</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Homarr"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Icone</Label>
            <div className="flex flex-wrap gap-1">
              {EMOJI_PICKER.map((em) => (
                <button
                  key={em}
                  type="button"
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded text-lg transition-colors",
                    icon === em
                      ? "bg-primary/20 ring-1 ring-primary"
                      : "hover:bg-muted"
                  )}
                  onClick={() => setIcon(em)}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Suggestions</Label>
            <div className="flex flex-wrap gap-1">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s.name}
                  type="button"
                  className="rounded bg-muted px-2 py-1 text-xs transition-colors hover:bg-muted/80"
                  onClick={() => {
                    setName(s.name);
                    setIcon(s.icon);
                  }}
                >
                  {s.icon} {s.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !url.trim()}
          >
            <Plus className="mr-2 h-4 w-4" />
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MyAppsPopover({ isAdmin = false }: { isAdmin?: boolean }) {
  const [tiles, setTiles] = useState<AppTile[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setTiles(JSON.parse(saved));
    } catch { /* ignore */ }
    setMounted(true);
  }, []);

  const saveTiles = (newTiles: AppTile[]) => {
    setTiles(newTiles);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newTiles));
    } catch { /* ignore */ }
  };

  const addTile = (tile: Omit<AppTile, "id">) => {
    saveTiles([...tiles, { ...tile, id: Date.now().toString() }]);
    setShowModal(false);
  };

  const removeTile = (id: string) => {
    saveTiles(tiles.filter((t) => t.id !== id));
  };

  if (!mounted) return null;

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
          >
            <LayoutGrid className="h-5 w-5" />
            <span className="sr-only">Mes applications</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold">Mes Applications</h4>
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowModal(true)}
              >
                <Plus className="mr-1 h-3 w-3" />
                Ajouter
              </Button>
            )}
          </div>

          {tiles.length === 0 && (
            <p className="py-4 text-center text-xs text-muted-foreground">
              Aucune application configurée
            </p>
          )}

          <div className="grid grid-cols-3 gap-2">
            {tiles.map((tile) => (
              <a
                key={tile.id}
                href={tile.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex flex-col items-center justify-center rounded-lg border p-2 transition-colors hover:bg-muted/50"
              >
                <span className="mb-1 text-xl">{tile.icon}</span>
                <span className="max-w-[60px] truncate text-[10px] text-muted-foreground">
                  {tile.name}
                </span>
                {isAdmin && (
                  <button
                    className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] group-hover:flex"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeTile(tile.id);
                    }}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </a>
            ))}

            {isAdmin && (
              <button
                className="flex flex-col items-center justify-center rounded-lg border border-dashed p-2 text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground/60"
                onClick={() => setShowModal(true)}
              >
                <Plus className="h-4 w-4" />
                <span className="mt-0.5 text-[10px]">Ajouter</span>
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <AddAppModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onAdd={addTile}
      />
    </>
  );
}
